import React, { useState } from 'react';
import { Datagrid, DateField, FunctionField, List, SelectInput, ShowButton, TextField, TextInput, TopToolbar, usePermissions, useTranslate } from 'react-admin';
import { Box, Button, Chip } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { hasPermission } from '../../utils/hasPermission';
import { UploadReleaseDialog } from './UploadReleaseDialog';
import { formatBytes } from './labels';
import { RELEASE_KINDS, RELEASE_STATUSES, type Release } from './types';

const statusColor = (status: string): 'success' | 'warning' | 'default' => (status === 'ready' ? 'success' : status === 'uploading' ? 'warning' : 'default');

export const ReleaseBadges = ({ record }: { record: Release }) => {
  const translate = useTranslate();
  const b = (key: string) => translate(`resources.kiosk-releases.badges.${key}`);
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {record.signatureVerifiedAt !== null && <Chip size="small" color="success" variant="outlined" label={b('signatureChecked')} />}
      {record.packageVerifiedAt !== null && <Chip size="small" color="success" variant="outlined" label={b('packageChecked')} />}
      {record.octopusAffecting && <Chip size="small" color="warning" label={b('touchesOctopus')} />}
      {record.journalBreaking && <Chip size="small" color="error" label={b('journalChange')} />}
      {!record.keyActive && <Chip size="small" color="error" variant="outlined" label={translate('resources.kiosk-releases.keyRetired')} />}
    </Box>
  );
};

const Actions = ({ onUpload }: { onUpload: () => void }) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  if (!hasPermission(permissions as string[] | null | undefined, 'kiosk-releases:write:all')) return <TopToolbar />;
  return (
    <TopToolbar>
      <Button variant="contained" startIcon={<CloudUpload />} onClick={onUpload} sx={{ textTransform: 'none' }}>
        {translate('resources.kiosk-releases.upload.button')}
      </Button>
    </TopToolbar>
  );
};

export const KioskReleasesList = () => {
  const translate = useTranslate();
  const [uploading, setUploading] = useState(false);
  const f = (key: string) => translate(`resources.kiosk-releases.fields.${key}`);
  const filters = [
    <TextInput key="q" source="q" label={f('version')} alwaysOn />,
    <SelectInput key="kind" source="kind" label={f('kind')} choices={RELEASE_KINDS.map((id) => ({ id, name: translate(`resources.kiosk-releases.kinds.${id}`) }))} alwaysOn />,
    <SelectInput key="status" source="status" label={f('status')} choices={RELEASE_STATUSES.map((id) => ({ id, name: translate(`resources.kiosk-releases.statuses.${id}`) }))} alwaysOn />,
  ];
  return (
    <>
      <List title={translate('resources.kiosk-releases.name')} filters={filters} actions={<Actions onUpload={() => setUploading(true)} />} sort={{ field: 'createdAt', order: 'DESC' }} exporter={false}>
        <Datagrid bulkActionButtons={false} rowClick="show">
          <TextField source="version" label={f('version')} />
          <FunctionField label={f('kind')} render={(record: Release) => translate(`resources.kiosk-releases.kinds.${record.kind}`)} />
          <FunctionField label={f('build')} render={(record: Release) => (record.build === null ? '-' : String(record.build))} />
          <FunctionField label={f('status')} render={(record: Release) => <Chip size="small" color={statusColor(record.status)} label={translate(`resources.kiosk-releases.statuses.${record.status}`)} />} />
          <FunctionField label={translate('resources.kiosk-releases.columns.key')} render={(record: Release) => (record.keyId === null ? '-' : record.keyId)} />
          <FunctionField label="" render={(record: Release) => <ReleaseBadges record={record} />} />
          <FunctionField label={f('oclReference')} render={(record: Release) => (record.oclReference === null ? '-' : record.oclReference)} />
          <FunctionField label={f('sizeBytes')} render={(record: Release) => formatBytes(record.sizeBytes)} />
          <FunctionField label={f('activeRollouts')} render={(record: Release) => String(record.activeRollouts)} />
          <DateField source="createdAt" label={f('createdAt')} showTime />
          <ShowButton />
        </Datagrid>
      </List>
      <UploadReleaseDialog open={uploading} onClose={() => setUploading(false)} />
    </>
  );
};
